import React from 'react';
import { useI18n } from 'next-localization';
import BaseSkeleton from '@/components/Generic/BaseSkeleton';
import Tooltip from 'components/Generic/Tooltip';

type Column = {
  visibleName: string;
  propertyName: string;
};

type Props = {
  columnsToShow: {
    general: Column[];
    shareclassSpecific: Column[];
  };
  showSkeleton?: boolean;
  hideShareclass?: boolean;
};

const FundsThead: React.FC<Props> = ({
  columnsToShow,
  showSkeleton = false,
  hideShareclass = false,
}) => {
  const { t } = useI18n();
  const showTooltip = (name: string) => {
    if (name === 'sfdrClassification') {
      const content = t('fundsFundCentreTooltipSfdr');
      return {
        show: content && content !== 'fundsFundCentreTooltipSfdr',
        tooltipContent: content,
      };
    }
    if (name === 'nav') {
      const content = t('fundsFundCentreTooltipNav');
      return {
        show: content && content !== 'fundsFundCentreTooltipNav',
        tooltipContent: content,
      };
    }
    return { show: false, tooltipContent: '' };
  };

  return (
    <thead className="fundsThead">
      {!showSkeleton ? (
        <tr>
          <th scope="col" className="section-header-small sticky-column">
            <div className="d-flex">{t('fundName')}</div>
          </th>

          {!hideShareclass && (
            <th scope="col" className="section-header-small">
              <div className="d-flex">
                {t('kidShareClass')}
                {t('fundsFundCentreTooltipShareclass') &&
                  t('fundsFundCentreTooltipShareclass') !== 'fundsFundCentreTooltipShareclass' && (
                    <Tooltip
                      tooltipTitle={t('kidShareClass')}
                      arialLabelText={t('kidShareClass')}
                      tooltipContentPosition="bottom"
                      tooltipContent={t('fundsFundCentreTooltipShareclass')}
                    />
                  )}
              </div>
            </th>
          )}

          {columnsToShow.general.map((col) => {
            const { show, tooltipContent } = showTooltip(col.propertyName);
            return (
              <th
                key={col.visibleName}
                scope="col"
                className={`section-header-small ${col.visibleName.length > 19 ? 'wrapText' : ''}`}
              >
                <div className="d-flex">
                  {col.visibleName}
                  {show && (
                    <Tooltip
                      arialLabelText={col.visibleName}
                      tooltipContentPosition="bottom"
                      tooltipTitle={col.visibleName}
                      tooltipContent={tooltipContent}
                    />
                  )}
                </div>
              </th>
            );
          })}

          {columnsToShow.shareclassSpecific.map((col) => {
            const { show, tooltipContent } = showTooltip(col.propertyName);
            return (
              <th
                key={col.visibleName}
                scope="col"
                className={`section-header-small ${col.visibleName.length > 19 ? 'wrapText' : ''}`}
              >
                {col.visibleName}
                {show && (
                  <Tooltip
                    arialLabelText={col.visibleName}
                    tooltipContentPosition="bottom"
                    tooltipTitle={col.visibleName}
                    tooltipContent={tooltipContent}
                  />
                )}
              </th>
            );
          })}
        </tr>
      ) : (
        <tr className="skeleton-row">
          {Array.from({ length: 6 }).map((_, idx) => (
            <th key={idx} scope="col" className="section-header-small">
              <BaseSkeleton />
            </th>
          ))}
        </tr>
      )}
    </thead>
  );
};

export default FundsThead;
